import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Printer, ArrowLeft } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// 4x6 inch thermal label sheet for PO items.
// No prices anywhere. Vendor name rendered tiny + faint on purpose.
export default function POLabelsPage({ mode = 'staff' }) {
  const { poId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        let res;
        if (mode === 'vendor') {
          const session = JSON.parse(localStorage.getItem('vendor_session') || '{}');
          res = await axios.get(`${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/labels-data`, {
            headers: { 'X-Vendor-Session': session.session_token || '' }
          });
        } else {
          const token = localStorage.getItem('token');
          res = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/labels-data`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        setData(res.data);
        const init = {};
        (res.data.labels || []).forEach((l, i) => { init[i] = l.quantity || 1; });
        setCounts(init);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load label data — please log in and try again');
      }
    };
    load();
  }, [poId, mode]);

  const printableLabels = useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.labels || []).forEach((l, i) => {
      const n = Math.max(0, parseInt(counts[i], 10) || 0);
      for (let k = 0; k < n; k++) out.push({ ...l, copy: k + 1, copies: n });
    });
    return out;
  }, [data, counts]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <p className="text-red-400 text-center" data-testid="labels-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <style>{`
        @media print {
          @page { size: 4in 6in; margin: 0; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .label-4x6 {
            width: 4in; height: 6in; page-break-after: always;
            margin: 0 !important; border: none !important; border-radius: 0 !important;
          }
        }
        .label-4x6 {
          width: 4in; height: 6in; background: #fff; color: #000;
          display: flex; flex-direction: column; overflow: hidden;
          padding: 0.18in; box-sizing: border-box; font-family: Arial, Helvetica, sans-serif;
        }
      `}</style>

      {/* Controls (hidden when printing) */}
      <div className="no-print p-4 max-w-lg mx-auto text-white">
        <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-gray-400 mb-3" data-testid="labels-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-xl font-bold mb-1">Print Labels — {data.po_number}</h1>
        <p className="text-sm text-gray-400 mb-4">4x6 thermal labels · one per unit · no prices shown</p>
        <div className="space-y-2 mb-4">
          {(data.labels || []).map((l, i) => (
            <div key={i} className="flex items-center gap-3 bg-gray-800 rounded-lg p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{l.product_title}</p>
                {l.variant_title && !/^default( title)?$/i.test((l.variant_title || '').trim()) && (
                  <p className="text-xs text-amber-400 truncate" data-testid={`label-variant-${i}`}>{l.variant_title}</p>
                )}
                <p className="text-xs text-gray-400">{l.sku}</p>
              </div>
              <label className="text-xs text-gray-400">Labels:</label>
              <input
                type="number" min="0" value={counts[i] ?? ''}
                onChange={(e) => setCounts({ ...counts, [i]: e.target.value })}
                className="w-16 p-2 rounded bg-gray-900 border border-gray-700 text-white text-sm"
                data-testid={`label-count-input-${i}`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={() => window.print()}
          disabled={printableLabels.length === 0}
          className="w-full py-3 rounded-xl bg-amber-500 text-black font-bold text-base flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="print-labels-btn"
        >
          <Printer className="w-5 h-5" /> Print {printableLabels.length} Label{printableLabels.length === 1 ? '' : 's'}
        </button>
      </div>

      {/* Labels */}
      <div className="flex flex-col items-center gap-4 pb-8 print:gap-0 print:pb-0">
        {printableLabels.map((l, idx) => (
          <div key={idx} className="label-4x6 rounded-lg border border-gray-700" data-testid={`po-label-${idx}`}>
            {/* Product photo */}
            <div style={{ height: '2.5in', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {l.image_url ? (
                <img src={l.image_url} alt={l.sku} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ color: '#999', fontSize: '12px' }}>No image</div>
              )}
            </div>
            {/* Product name */}
            <div style={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.25, marginTop: '0.08in', maxHeight: '0.55in', overflow: 'hidden' }}>
              {l.product_title}
            </div>
            {l.variant_title && !/^default( title)?$/i.test((l.variant_title || '').trim()) && (
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginTop: '0.02in' }}>
                {l.variant_title}
              </div>
            )}
            {/* SKU — big and bold for scanning eyes */}
            <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '0.5px', marginTop: '0.04in' }}>
              {l.sku}
            </div>
            {/* PO + date + copy count */}
            <div style={{ fontSize: '11px', color: '#333', marginTop: '0.03in' }}>
              {data.po_number} · {data.date}{l.copies > 1 ? ` · ${l.copy}/${l.copies}` : ''}
            </div>
            {/* QR + footer */}
            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '8px', color: '#aaa', paddingBottom: '0.04in', maxWidth: '2.2in' }}>
                <div>Scan QR to update / receive</div>
                {/* vendor name intentionally tiny + faint */}
                <div style={{ marginTop: '2px' }}>{data.vendor_name}</div>
              </div>
              {data.qr_data_uri && (
                <img src={data.qr_data_uri} alt="QR" style={{ width: '1.1in', height: '1.1in' }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
